"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { API_BASE, extractErrorMessage } from "@/app/lib/api";

export interface AuthUser {
  id: number;
  email: string;
  full_name: string | null;
}

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  /** True only while the initial session (token -> user) is being restored. */
  loading: boolean;
  login: (email: string, password: string, turnstileToken: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    fullName: string,
    turnstileToken: string
  ) => Promise<void>;
  /**
   * Finish a sign-in that happened outside this app (Google OAuth): the
   * backend already issued the JWT, so there is nothing to authenticate —
   * store the token the same way `login` does and load the profile with it.
   * Throws if the token is rejected, so the caller can show an error rather
   * than a half-initialized session.
   */
  completeSignIn: (accessToken: string) => Promise<void>;
  logout: () => void;
}

const TOKEN_STORAGE_KEY = "jobplatform_token";

const AuthContext = createContext<AuthContextValue | null>(null);

function readStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredToken(token: string | null) {
  try {
    if (token) {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  } catch {
    // Storage can throw in private-browsing/blocked-cookie contexts; the
    // session just won't persist across reloads, which is an acceptable fallback.
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async (authToken: string): Promise<AuthUser | null> => {
    const res = await fetch(`${API_BASE}/auth/me`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!res.ok) return null;
    return res.json();
  }, []);

  // Restore a previous session from localStorage on first load.
  useEffect(() => {
    let cancelled = false;

    async function init() {
      const stored = readStoredToken();
      if (!stored) {
        if (!cancelled) setLoading(false);
        return;
      }

      const me = await fetchMe(stored);
      if (cancelled) return;

      if (me) {
        setToken(stored);
        setUser(me);
      } else {
        writeStoredToken(null);
      }
      setLoading(false);
    }

    init();
    return () => {
      cancelled = true;
    };
  }, [fetchMe]);

  const login = useCallback(
    async (email: string, password: string, turnstileToken: string) => {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, turnstile_token: turnstileToken }),
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, "Login failed. Please try again."));
      }

      const data: { access_token: string } = await res.json();
      const me = await fetchMe(data.access_token);
      if (!me) {
        throw new Error("Signed in, but couldn't load your profile. Please try again.");
      }

      writeStoredToken(data.access_token);
      setToken(data.access_token);
      setUser(me);
    },
    [fetchMe]
  );

  const register = useCallback(
    async (email: string, password: string, fullName: string, turnstileToken: string) => {
      const res = await fetch(`${API_BASE}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          password,
          full_name: fullName || null,
          turnstile_token: turnstileToken,
        }),
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, "Registration failed. Please try again."));
      }

      // Register also returns an access token (auto-login) so we don't need
      // a second Turnstile challenge just to sign the new user in.
      const data: { access_token: string } = await res.json();
      const me = await fetchMe(data.access_token);
      if (!me) {
        throw new Error("Account created, but couldn't load your profile. Please sign in.");
      }

      writeStoredToken(data.access_token);
      setToken(data.access_token);
      setUser(me);
    },
    [fetchMe]
  );

  const completeSignIn = useCallback(
    async (accessToken: string) => {
      const me = await fetchMe(accessToken);
      if (!me) {
        throw new Error("Signed in, but couldn't load your profile. Please sign in again.");
      }

      writeStoredToken(accessToken);
      setToken(accessToken);
      setUser(me);
    },
    [fetchMe]
  );

  const logout = useCallback(() => {
    writeStoredToken(null);
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, token, loading, login, register, completeSignIn, logout }),
    [user, token, loading, login, register, completeSignIn, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
